/**
 * Update checker — branches per platform because Android can't auto-install.
 *
 * Desktop (Tauri 2 plugin-updater):
 *   - Checks the endpoint baked into tauri.conf.json (`pubkey` is also there).
 *   - If an update exists, exposes `install()` → downloads + verifies +
 *     restarts the app via the plugin's `downloadAndInstall()`.
 *
 * Android (in-Tauri but no updater plugin support):
 *   - Plain `fetch()` on the same `updates.json`.
 *   - `install()` opens the APK URL in the system browser; user installs by
 *     hand. There's nothing more we can do — Android's sandbox blocks
 *     in-place replacement of an installed APK.
 *
 * Web SPA (no Tauri):
 *   - Returns `{available:false}`. Web users don't install Console; they ARE
 *     the console (cloud Hub thin-client). Browser auto-updates via the
 *     service worker / Cloudflare Pages, not this flow.
 */

import { IS_TAURI } from './hubUrl';

export type UpdaterPlatform = 'desktop' | 'android' | 'web';

/**
 * Progress events forwarded from Tauri's `downloadAndInstall`. The dialog
 * uses these to show meaningful state instead of an indefinite spinner.
 *
 *   - `download-started` — install begins, with optional total content length
 *   - `download-progress` — incremental bytes received (sum the deltas)
 *   - `download-finished` — bytes done, install phase about to start (pkexec
 *     prompt on Linux fires here)
 *   - `install-finished` — `.deb` installed; app needs a manual relaunch on
 *     Linux because mid-execution binary replacement isn't reliable
 */
export type UpdateEvent =
  | { kind: 'download-started'; total: number | null }
  | { kind: 'download-progress'; delta: number }
  | { kind: 'download-finished' }
  | { kind: 'install-finished' };

export interface UpdateInfo {
  available: boolean;
  /** Manifest version string, e.g. "0.1.1". Empty when no update. */
  version: string;
  /** Release notes; may be empty. */
  notes: string;
  /** ISO timestamp from manifest. */
  pubDate: string;
  /** APK URL on Android; not used elsewhere. */
  androidUrl?: string;
  /** Trigger the install (or open browser on Android). The callback fires
   *  with progress events on desktop. Resolves AFTER the install step on
   *  Linux (Tauri's auto-restart is unreliable there) — caller should
   *  prompt the user to relaunch manually. Throws on signature failure. */
  install: (onEvent?: (e: UpdateEvent) => void) => Promise<void>;
}

const MIRROR_URL = 'https://hub.bramer.in/downloads/updates.json';
const PLATFORM_KEY_ANDROID = 'android-universal';

export function platform(): UpdaterPlatform {
  if (!IS_TAURI) return 'web';
  if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)) {
    return 'android';
  }
  return 'desktop';
}

/** Resolved manifest from `updates.json`. */
interface ManifestEntry {
  signature?: string;
  url: string;
}
interface Manifest {
  version: string;
  notes?: string;
  pub_date?: string;
  platforms: Record<string, ManifestEntry>;
}

/**
 * Returns update info, or `null` if no update available / the platform doesn't
 * support updates. Caller decides UX (dialog for desktop, toast for android,
 * nothing for web).
 */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  const p = platform();
  if (p === 'web') return null;

  if (p === 'desktop') {
    return checkDesktop();
  }
  return checkAndroid();
}

async function checkDesktop(): Promise<UpdateInfo | null> {
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update?.available) return null;
    return {
      available: true,
      version: update.version,
      notes: update.body ?? '',
      pubDate: update.date ?? '',
      install: async (onEvent) => {
        await update.downloadAndInstall((evt) => {
          if (!onEvent) return;
          // Tauri's event payloads vary by platform; map to our flat shape.
          switch (evt.event) {
            case 'Started':
              onEvent({
                kind: 'download-started',
                total: typeof evt.data?.contentLength === 'number'
                  ? evt.data.contentLength
                  : null,
              });
              break;
            case 'Progress':
              onEvent({ kind: 'download-progress', delta: evt.data?.chunkLength ?? 0 });
              break;
            case 'Finished':
              onEvent({ kind: 'download-finished' });
              break;
          }
        });
        // On Linux, downloadAndInstall returns AFTER `dpkg -i` completes
        // (auth via pkexec, then install). Tauri tries to relaunch, but
        // mid-execution binary replacement is flaky here — the dialog
        // should explicitly tell the user to restart.
        onEvent?.({ kind: 'install-finished' });
      },
    };
  } catch (e) {
    console.warn('updater check failed', e);
    return null;
  }
}

async function checkAndroid(): Promise<UpdateInfo | null> {
  try {
    const res = await fetch(MIRROR_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const manifest = (await res.json()) as Manifest;
    if (!isNewer(manifest.version, currentAppVersion())) return null;
    const entry = manifest.platforms[PLATFORM_KEY_ANDROID];
    if (!entry?.url) return null;
    return {
      available: true,
      version: manifest.version,
      notes: manifest.notes ?? '',
      pubDate: manifest.pub_date ?? '',
      androidUrl: entry.url,
      install: async () => {
        // Open the APK download in whatever default browser the WebView
        // hands off to. Android takes over from there.
        window.location.href = entry.url;
      },
    };
  } catch (e) {
    console.warn('android update check failed', e);
    return null;
  }
}

/** Strict semver-ish compare. Returns true if `candidate` > `current`. */
export function isNewer(candidate: string, current: string): boolean {
  const parse = (v: string) =>
    v.replace(/^v/, '').split('.').map((n) => parseInt(n, 10) || 0);
  const a = parse(candidate);
  const b = parse(current);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai > bi) return true;
    if (ai < bi) return false;
  }
  return false;
}

/** Pulled from Vite's build-time __APP_VERSION__ when the app was bundled.
 *  Falls back to "0.0.0" in dev / when the constant isn't injected. */
declare const __APP_VERSION__: string | undefined;
export function currentAppVersion(): string {
  try {
    return typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__
      ? __APP_VERSION__
      : '0.0.0';
  } catch {
    return '0.0.0';
  }
}

// ── localStorage keys for skip / dismiss state ───────────────────────────────

const SKIP_KEY = 'console.updater.skipped_version';
const SEEN_KEY = 'console.updater.seen_version';

export function getSkippedVersion(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SKIP_KEY);
}

export function setSkippedVersion(v: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SKIP_KEY, v);
}

export function getSeenVersion(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(SEEN_KEY);
}

export function markSeen(v: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SEEN_KEY, v);
}
